import {
  DISPLAY_NAME,
  GAME_NAME,
  type LinkCode,
  type Units,
  validateDisplayName,
} from '@geosport/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../components/Button';
import { DigestSettings } from '../components/Digest';
import { ReminderSettings } from '../components/Reminder';
import { track } from '../lib/analytics';
import { api, ApiError, setPlayerToken } from '../lib/api';
import { analyticsAllowed, useConsent, type Consent } from '../lib/consent';
import { disableGa } from '../lib/ga';
import { useInstallPrompt } from '../lib/install';
import { useSettings, type MotionPref } from '../lib/settings';
import { clearAllStorage, readString, removeKey, STORAGE_KEYS, writeString } from '../lib/storage';
import type { MapStyleKind } from '../map/style';

const NAME_KEY = `${STORAGE_KEYS.settings}:name`;

type Status = { kind: 'idle' | 'saving' | 'ok' | 'error'; text?: string };

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-2">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={value === v}
          className={`min-h-11 flex-1 rounded-lg px-3 text-sm font-semibold ${
            value === v ? 'bg-accent text-ink' : 'bg-white/10 text-white/80'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Move this identity to another device: mint a code here, type it there. */
function Devices() {
  const [code, setCode] = useState<LinkCode | null>(null);
  const [entry, setEntry] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);

  const mint = async (): Promise<void> => {
    setBusy(true);
    try {
      setCode(await api<LinkCode>('/me/link-code', { method: 'POST', body: {} }));
      setStatus({ kind: 'idle' });
    } catch (err) {
      setStatus({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Could not get a code',
      });
    } finally {
      setBusy(false);
    }
  };

  const link = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api<{ playerToken: string }>('/me/link', {
        method: 'POST',
        body: { code: entry },
      });
      setPlayerToken(res.playerToken);
      removeKey(STORAGE_KEYS.progress);
      removeKey(STORAGE_KEYS.practiceProgress);
      removeKey(NAME_KEY);
      track('device_linked');
      setStatus({ kind: 'ok', text: 'Linked! Reloading…' });
      window.setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof ApiError ? err.message : 'Could not link' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="mb-5 rounded-xl bg-white/5 p-3">
      <legend className="px-1 text-sm text-white/70">Use on another device</legend>
      <p className="text-xs text-white/50">
        No account needed. Get a code here, enter it on your other device, and your streak, stats
        and groups follow you.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <Button variant="secondary" onClick={() => void mint()} disabled={busy} size="sm">
          Get a code
        </Button>
        {code && (
          <span className="font-mono text-2xl font-bold tracking-[0.2em]" data-testid="link-code">
            {code.code}
          </span>
        )}
      </div>
      {code && <p className="mt-1 text-xs text-white/50">Valid for 10 minutes, single use.</p>}
      <form onSubmit={(e) => void link(e)} className="mt-3 flex gap-2">
        <input
          value={entry}
          onChange={(e) => setEntry(e.target.value.toUpperCase())}
          placeholder="Enter a code from another device"
          maxLength={8}
          aria-label="Link code"
          autoCapitalize="characters"
          className="min-h-11 flex-1 rounded-lg bg-white/10 px-3 font-mono tracking-widest text-white placeholder:font-sans placeholder:tracking-normal placeholder:text-white/30"
        />
        <Button type="submit" variant="secondary" disabled={busy || entry.length < 6}>
          Link
        </Button>
      </form>
      {status.text && (
        <p
          className={`mt-1 text-sm ${status.kind === 'error' ? 'text-red-300' : 'text-emerald-300'}`}
          role="status"
        >
          {status.text}
        </p>
      )}
    </fieldset>
  );
}

/** The visitor's analytics choice; before any answer it shows what currently applies. */
export function AnalyticsChoice() {
  const choice = useConsent((s) => s.choice);
  const choose = useConsent((s) => s.choose);
  return (
    <fieldset className="mb-5">
      <legend className="mb-1 text-sm text-white/70">Analytics cookies (Google Analytics)</legend>
      <Segmented<Consent>
        value={choice ?? (analyticsAllowed(null) ? 'granted' : 'denied')}
        onChange={choose}
        options={[
          ['granted', 'Allow'],
          ['denied', 'Off'],
        ]}
      />
      <p className="mt-1 text-xs text-white/50">
        Counts visits and which features get used. No ads and no cross-site profile.
      </p>
    </fieldset>
  );
}

function DangerZone({ onClose }: { onClose: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wipe = async (): Promise<void> => {
    setBusy(true);
    try {
      await api('/me', { method: 'DELETE' });
      track('data_deleted');
      clearAllStorage();
      disableGa();
      onClose();
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete your data');
      setBusy(false);
    }
  };
  return (
    <fieldset className="rounded-xl bg-red-500/10 p-3 ring-1 ring-red-500/30">
      <legend className="px-1 text-sm text-red-200">Delete my data</legend>
      <p className="text-xs text-white/60">
        Removes your scores, streak, name and group memberships from {GAME_NAME}. This cannot be
        undone.
      </p>
      {!confirming ? (
        <Button
          variant="secondary"
          size="sm"
          className="mt-2 text-red-200"
          onClick={() => setConfirming(true)}
        >
          Delete my data…
        </Button>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            className="bg-red-500 text-white hover:bg-red-400"
            onClick={() => void wipe()}
            disabled={busy}
          >
            Yes, delete everything
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-300" role="status">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export function SettingsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const units = useSettings((s) => s.units);
  const motion = useSettings((s) => s.motion);
  const mapStyle = useSettings((s) => s.mapStyle);
  const setUnits = useSettings((s) => s.setUnits);
  const setMotion = useSettings((s) => s.setMotion);
  const setMapStyle = useSettings((s) => s.setMapStyle);
  const install = useInstallPrompt();
  const [name, setName] = useState(() => readString(NAME_KEY) ?? '');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const saveName = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const check = validateDisplayName(name);
    if (!check.ok) {
      setStatus({ kind: 'error', text: check.reason });
      return;
    }
    setStatus({ kind: 'saving' });
    try {
      const res = await api<{ displayName: string }>('/me/name', {
        method: 'POST',
        body: { name: check.name },
      });
      setName(res.displayName);
      writeString(NAME_KEY, res.displayName);
      setStatus({
        kind: 'ok',
        text:
          res.displayName === check.name ? 'Saved' : `Saved as ${res.displayName} (name was taken)`,
      });
      track('name_set');
    } catch (err) {
      setStatus({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Could not save your name',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-ink-2 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="settings-title" className="text-lg font-bold">
            Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="min-h-11 min-w-11 rounded-lg text-xl hover:bg-white/10"
          >
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <form onSubmit={(e) => void saveName(e)} className="mb-5">
          <label htmlFor="display-name" className="mb-1 block text-sm text-white/70">
            Display name (shown on leaderboards)
          </label>
          <div className="flex gap-2">
            <input
              id="display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              minLength={DISPLAY_NAME.min}
              maxLength={DISPLAY_NAME.max}
              placeholder="e.g. Sunday League"
              autoComplete="nickname"
              className="min-h-11 flex-1 rounded-lg bg-white/10 px-3 text-white placeholder:text-white/30"
            />
            <Button type="submit" variant="secondary" disabled={status.kind === 'saving'}>
              Save
            </Button>
          </div>
          {status.text && (
            <p
              className={`mt-1 text-sm ${status.kind === 'error' ? 'text-red-300' : 'text-emerald-300'}`}
              role="status"
            >
              {status.text}
            </p>
          )}
        </form>

        <fieldset className="mb-4">
          <legend className="mb-1 text-sm text-white/70">Map</legend>
          <Segmented<MapStyleKind>
            value={mapStyle}
            onChange={setMapStyle}
            options={[
              ['satellite', 'Satellite'],
              ['dark', 'Dark'],
            ]}
          />
        </fieldset>

        <fieldset className="mb-4">
          <legend className="mb-1 text-sm text-white/70">Distance units</legend>
          <Segmented<Units>
            value={units}
            onChange={setUnits}
            options={[
              ['km', 'Kilometres'],
              ['mi', 'Miles'],
            ]}
          />
        </fieldset>

        <fieldset className="mb-5">
          <legend className="mb-1 text-sm text-white/70">Motion</legend>
          <Segmented<MotionPref>
            value={motion}
            onChange={setMotion}
            options={[
              ['system', 'System'],
              ['full', 'Animate'],
              ['reduce', 'Reduce'],
            ]}
          />
        </fieldset>

        <ReminderSettings />
        <DigestSettings />

        <AnalyticsChoice />

        {(install.canInstall || install.iosHint) && (
          <fieldset className="mb-5 rounded-xl bg-white/5 p-3">
            <legend className="px-1 text-sm text-white/70">Get the app</legend>
            <p className="text-xs text-white/50">
              {install.canInstall
                ? `Install ${GAME_NAME} on your home screen. It opens instantly and works like an app.`
                : 'On iPhone: tap Share, then “Add to Home Screen”.'}
            </p>
            {install.canInstall && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => void install.prompt()}
              >
                Install
              </Button>
            )}
          </fieldset>
        )}

        <Devices />
        <DangerZone onClose={onClose} />
      </div>
    </div>
  );
}
